import { UserOutlined, MailOutlined, LockOutlined, FileTextOutlined, AuditOutlined } from '@ant-design/icons'
import { FormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { getRegisterInitialValues } from './helpers/get-register-initial-values'
import { RegisterForm } from './register.interface'
import { RegisterValidationSchema } from './validators/register-validation-schema'
import { ApiResponseStatus } from '../../../common/api/constants/api-response.status'
import { useFormik } from '../../../common/hooks/formik'
import { PureButton } from '../../../common/ui/pure-button/pure-button'
import { PureInput } from '../../../common/ui/pure-input/pure-input'
import { RouteEnum } from '../../../constants/routes.enum'
import { useRegister } from '../../../entities/auth/hooks/auth.hooks'
import { RegisterInfo } from '../../../entities/auth/types/auth.types'
import { CompanyData } from '../../../entities/dadata/types/dadata.types'
import { useCheckOrganization, useGetOrganization } from '../../../entities/organizations/hooks/organization.hooks'
import { useGetUserInvitation } from '../../../entities/user/hooks/user.hooks'
import { PageHeader } from '../../shared/components/page-header'
import { SearchCompany } from '../../shared/components/search-company/search-company'

export const Register = () => {
  const { t } = useTranslation()
  const { token } = useParams()
  const navigate = useNavigate()
  const { mutateAsync: register } = useRegister()
  const { mutateAsync: checkOrganization } = useCheckOrganization()
  const { data: userInvitation } = useGetUserInvitation(token)
  const { data: userOrganization } = useGetOrganization(userInvitation?.organization)

  const formik = useFormik<RegisterForm>({
    initialValues: getRegisterInitialValues({ ...userOrganization, email: userInvitation?.email ?? null }),
    validationSchema: RegisterValidationSchema(),
    enableReinitialize: true,
    onSubmit: () => {},
  })

  const handleSubmit = async () => {
    const err = await formik.validateForm()
    if (Object.values(err).length) {
      return
    }
    const isOrganizationExists = await checkOrganization(formik.values.companyOgrn || '')
    if (isOrganizationExists.exists && !userInvitation) {
      toast.error(t('auth.register.errors.companyExists'))

      return
    }

    try {
      const registerResult = await register({ ...formik.values, invitationToken: token } as RegisterInfo)
      if (registerResult.status === ApiResponseStatus.Success) {
        toast.success(t('auth.register.success'))
        navigate('/')
      }
    } catch {
      toast.error(t('auth.register.errors.registerError'))
    }
  }

  const handleSearchCompany = (company: CompanyData) => {
    formik.setValues({
      ...formik.values,
      companyName: company.value,
      companyInn: company.data.inn,
      companyOgrn: company.data.ogrn,
    })
  }

  return (
    <FormikContext.Provider value={formik as any}>
      <PageHeader header={t('auth.register.header')} />
      <div className="step-wrapper row bg-white rounded-4 pb-5 pt-4 px-3 px-md-5 mb-5">
        <PureInput
          {...formik.getFieldProps('fio')}
          colClass="col-12 col-md-6"
          error={formik.errors.fio}
          icon={<UserOutlined />}
          label={t('auth.register.fio')}
          moduleStyle="mb-4"
        />
        <PureInput
          {...formik.getFieldProps('email')}
          colClass="col-12 col-md-6"
          disabled={!!userInvitation?.email}
          error={formik.errors.email}
          icon={<MailOutlined />}
          label={t('auth.register.email')}
          moduleStyle="mb-4"
        />
        {!token && (
          <SearchCompany
            colClass="col-12 col-md-12 mb-4"
            initialValue={formik.values.companyName ?? undefined}
            label={t('auth.register.companySearch')}
            onChange={handleSearchCompany}
          />
        )}
        <PureInput
          {...formik.getFieldProps('companyName')}
          colClass="col-12 col-md-6"
          disabled={!!userInvitation?.email}
          error={formik.errors.companyName}
          icon={<AuditOutlined />}
          label={t('auth.register.companyName')}
          moduleStyle="mb-4"
          value={userOrganization?.name ?? formik.getFieldProps('companyName').value ?? undefined}
          required
        />
        <PureInput
          {...formik.getFieldProps('companyInn')}
          colClass="col-12 col-md-6"
          disabled={!!userInvitation?.email}
          error={formik.errors.companyInn}
          icon={<FileTextOutlined />}
          label={t('auth.register.companyInn')}
          moduleStyle="mb-4"
          value={userOrganization?.companyInn ?? formik.getFieldProps('companyInn').value ?? undefined}
        />
        <PureInput
          {...formik.getFieldProps('companyOgrn')}
          colClass="col-12 col-md-6"
          disabled={!!userInvitation?.email}
          error={formik.errors.companyOgrn}
          icon={<FileTextOutlined />}
          label={t('auth.register.companyOgrn')}
          moduleStyle="mb-4"
          value={userOrganization?.companyOgrn ?? formik.getFieldProps('companyOgrn').value ?? undefined}
        />
        <div></div>
        <PureInput
          {...formik.getFieldProps('password')}
          colClass="col-12 col-md-6"
          error={formik.errors.password}
          icon={<LockOutlined />}
          label={t('auth.register.password')}
          moduleStyle="mb-4"
          type="password"
        />
        <PureInput
          {...formik.getFieldProps('passwordConfirm')}
          colClass="col-12 col-md-6"
          error={formik.errors.passwordConfirm}
          icon={<LockOutlined />}
          label={t('auth.register.passwordConfirm')}
          moduleStyle="mb-4"
          type="password"
        />
        <div className="d-flex justify-content-between mt-4">
          <PureButton onClick={() => navigate(`/${RouteEnum.AUTH}`)}>{t('auth.prevButton')}</PureButton>
          <div className="col-4 col-md-3">
            <PureButton
              onClick={handleSubmit}
              type="primary"
              block
            >
              {t('auth.register.buttonNext')}
            </PureButton>
          </div>
        </div>
      </div>
    </FormikContext.Provider>
  )
}
